import { useTranslation } from 'react-i18next'
import { Languages } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SUPPORTED_LANGUAGES, setAppLanguage, type AppLanguage } from '@/i18n'
import { isDevEnvironment } from '@/lib/env'

const LANGUAGE_LABELS: Record<AppLanguage, string> = {
  fr: 'Français',
  en: 'English',
}

// Dev/local-only language switcher. Changing language updates the UI (i18next)
// AND the Accept-Language header the API client sends on every request, so the
// backend localizes its responses to match. Rendered nowhere in production
// (see lib/env). Kept out of the i18n bundle labels on purpose — this is a dev
// affordance, so its own text is plain.
export function LanguageSwitcher() {
  const { i18n } = useTranslation()

  if (!isDevEnvironment) return null

  const current = (i18n.language?.split('-')[0] as AppLanguage) ?? 'fr'

  const select = (lng: AppLanguage) => {
    if (lng === current) return
    // Persist the choice, then hard-reload. Several label maps (job status /
    // priority, invoice status, ...) resolve i18n.t() at module load, so they
    // only rebuild for the new language on a fresh page load. Reloading also
    // guarantees every API request goes out with the new Accept-Language.
    setAppLanguage(lng)
    window.location.reload()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className='gap-1.5'
          aria-label='Changer de langue (dev uniquement)'
          title='Changer de langue (dev uniquement)'
        >
          <Languages className='h-4 w-4' />
          <span className='text-xs font-semibold uppercase'>{current}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {SUPPORTED_LANGUAGES.map(lng => (
          <DropdownMenuItem
            key={lng}
            onClick={() => select(lng)}
            className={lng === current ? 'font-semibold' : undefined}
          >
            {LANGUAGE_LABELS[lng]}
            <span className='ml-2 text-xs uppercase text-muted-foreground'>
              {lng}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
